import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  Unsubscribe,
  increment,
  serverTimestamp,
  writeBatch,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from './firebase';
import { attendeeDisplayName } from '../utils/attendeeDisplayName';
import { createPrize, awardPrize } from './prizes';
import type { PrizeType } from './prizes';

export type PredictionGameStatus = 'countdown' | 'ready' | 'active' | 'revealing' | 'completed' | 'cancelled';
export type PredictionTieBreak = 'speed' | 'random' | 'shared';

export interface PredictionGame {
  id: string;
  eventId: string;
  djId: string;
  status: PredictionGameStatus;
  songDelay: number;
  songsRemaining: number;
  countdownVisible: boolean;
  tieBreakMethod: PredictionTieBreak;
  answerLocked: boolean;
  revealedSongRequestId: string | null;
  prizeType: PrizeType;
  prizeCustomText?: string;
  prizeId?: string;
  playerCount: number;
  round1Survivors: number;
  round2Survivors: number;
  round3Survivors: number;
  round4Survivors: number;
  winnerIds: string[];
  winnerNicknames: string[];
  previousHideRankingInfo: boolean;
  startedAt?: string;
  revealedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface PredictionAnswer {
  eventId: string;
  requestId: string;
  lockedAt: string;
}

export interface PredictionSelection {
  id: string;
  gameId: string;
  eventId: string;
  attendeeId: string;
  nickname?: string;
  optedIn: boolean;
  round1Picks?: string[];
  round1Eliminated?: boolean;
  round2Picks?: string[];
  round2Eliminated?: boolean;
  round3Picks?: string[];
  round3Eliminated?: boolean;
  round4Picks?: string[];
  round4Correct?: boolean;
  finishedAt?: string;
  createdAt: string;
  updatedAt: string;
}

function docToGame(docSnap: any): PredictionGame {
  const data = docSnap.data();
  return {
    id: docSnap.id,
    eventId: data.eventId,
    djId: data.djId,
    status: data.status,
    songDelay: data.songDelay ?? 0,
    songsRemaining: data.songsRemaining ?? 0,
    countdownVisible: data.countdownVisible ?? true,
    tieBreakMethod: data.tieBreakMethod ?? 'speed',
    answerLocked: data.answerLocked ?? false,
    revealedSongRequestId: data.revealedSongRequestId || null,
    prizeType: data.prizeType ?? 'shoutout',
    prizeCustomText: data.prizeCustomText || undefined,
    prizeId: data.prizeId || undefined,
    playerCount: data.playerCount ?? 0,
    round1Survivors: data.round1Survivors ?? 0,
    round2Survivors: data.round2Survivors ?? 0,
    round3Survivors: data.round3Survivors ?? 0,
    round4Survivors: data.round4Survivors ?? 0,
    winnerIds: data.winnerIds || [],
    winnerNicknames: data.winnerNicknames || [],
    previousHideRankingInfo: data.previousHideRankingInfo ?? false,
    startedAt: data.startedAt || undefined,
    revealedAt: data.revealedAt || undefined,
    createdAt: data.createdAt,
    updatedAt: data.updatedAt,
  };
}

function docToSelection(docSnap: any): PredictionSelection {
  const data = docSnap.data();
  return {
    id: docSnap.id,
    gameId: data.gameId,
    eventId: data.eventId,
    attendeeId: data.attendeeId,
    nickname: data.nickname || undefined,
    optedIn: data.optedIn ?? false,
    round1Picks: data.round1Picks || undefined,
    round1Eliminated: data.round1Eliminated ?? undefined,
    round2Picks: data.round2Picks || undefined,
    round2Eliminated: data.round2Eliminated ?? undefined,
    round3Picks: data.round3Picks || undefined,
    round3Eliminated: data.round3Eliminated ?? undefined,
    round4Picks: data.round4Picks || undefined,
    round4Correct: data.round4Correct ?? undefined,
    finishedAt: data.finishedAt || undefined,
    createdAt: data.createdAt,
    updatedAt: data.updatedAt,
  };
}

function answerRef(gameId: string) {
  return doc(db, 'predictionGames', gameId, 'private', 'answer');
}

export async function createPredictionGame(
  eventId: string,
  djId: string,
  config: {
    songDelay: number;
    countdownVisible: boolean;
    tieBreakMethod: PredictionTieBreak;
    prizeType: PrizeType;
    prizeCustomText?: string;
    previousHideRankingInfo: boolean;
  }
): Promise<string> {
  const now = new Date().toISOString();
  const gameRef = doc(collection(db, 'predictionGames'));
  const gameId = gameRef.id;

  const prizeId = await createPrize(eventId, djId, config.prizeType, {
    customText: config.prizeCustomText,
    gameSource: 'prediction',
    gameRoundId: `prediction-${gameId}`,
  });

  const initialStatus: PredictionGameStatus = config.songDelay === 0 ? 'ready' : 'countdown';

  await setDoc(gameRef, {
    eventId,
    djId,
    status: initialStatus,
    songDelay: config.songDelay,
    songsRemaining: config.songDelay,
    countdownVisible: config.countdownVisible,
    tieBreakMethod: config.tieBreakMethod,
    answerLocked: false,
    revealedSongRequestId: null,
    prizeType: config.prizeType,
    prizeCustomText: config.prizeCustomText || null,
    prizeId,
    playerCount: 0,
    round1Survivors: 0,
    round2Survivors: 0,
    round3Survivors: 0,
    round4Survivors: 0,
    winnerIds: [],
    winnerNicknames: [],
    previousHideRankingInfo: config.previousHideRankingInfo,
    createdAt: now,
    updatedAt: now,
  });

  await updateDoc(doc(db, 'events', eventId), {
    'settings.hideRankingInfo': true,
    'settings.predictionGameActive': true,
    'settings.predictionGameId': gameId,
    updatedAt: serverTimestamp(),
  });

  return gameId;
}

export async function decrementCountdown(gameId: string): Promise<void> {
  const gameRef = doc(db, 'predictionGames', gameId);
  const snap = await getDoc(gameRef);
  if (!snap.exists()) throw new Error('Game not found');

  const data = snap.data();
  if (data.status !== 'countdown') return;

  const newRemaining = (data.songsRemaining ?? 1) - 1;
  const now = new Date().toISOString();

  if (newRemaining <= 0) {
    await updateDoc(gameRef, {
      status: 'ready',
      songsRemaining: 0,
      updatedAt: now,
    });
  } else {
    await updateDoc(gameRef, {
      songsRemaining: newRemaining,
      updatedAt: now,
    });
  }
}

export async function lockAnswerSong(gameId: string, requestId: string): Promise<void> {
  const gameRef = doc(db, 'predictionGames', gameId);
  const snap = await getDoc(gameRef);
  if (!snap.exists()) throw new Error('Game not found');

  const data = snap.data();
  if (data.status !== 'ready' && data.status !== 'countdown') {
    throw new Error('Cannot lock answer song in current state');
  }

  const now = new Date().toISOString();
  const batch = writeBatch(db);
  batch.set(answerRef(gameId), { eventId: data.eventId, requestId, lockedAt: now });
  batch.update(gameRef, { answerLocked: true, updatedAt: now });
  await batch.commit();
}

export async function confirmAndDeploy(gameId: string): Promise<void> {
  const gameRef = doc(db, 'predictionGames', gameId);
  const snap = await getDoc(gameRef);
  if (!snap.exists()) throw new Error('Game not found');

  const data = snap.data();
  if (data.status !== 'ready') throw new Error('Game must be in ready state to deploy');
  if (!data.answerLocked) throw new Error('Must lock an answer song before deploying');

  const now = new Date().toISOString();
  await updateDoc(gameRef, {
    status: 'active',
    startedAt: now,
    updatedAt: now,
  });
}

export async function optInPlayer(
  gameId: string,
  eventId: string,
  attendeeId: string,
  nickname?: string
): Promise<void> {
  const selectionId = `${gameId}_${attendeeId}`;
  const now = new Date().toISOString();

  const batch = writeBatch(db);
  batch.set(doc(db, 'predictionSelections', selectionId), {
    gameId,
    eventId,
    attendeeId,
    nickname: nickname || null,
    optedIn: true,
    createdAt: now,
    updatedAt: now,
  });
  batch.update(doc(db, 'predictionGames', gameId), {
    playerCount: increment(1),
    updatedAt: now,
  });
  await batch.commit();
}

export async function submitRoundPicks(
  gameId: string,
  round: 1 | 2 | 3 | 4,
  picks: string[]
): Promise<{ eliminated: boolean; survivorCount: number }> {
  const expectedCounts: Record<number, number> = { 1: 5, 2: 3, 3: 2, 4: 1 };
  if (picks.length !== expectedCounts[round]) {
    throw new Error(`Round ${round} requires exactly ${expectedCounts[round]} picks`);
  }

  const callable = httpsCallable<
    { gameId: string; round: number; picks: string[] },
    { eliminated: boolean; survivorCount: number }
  >(functions, 'submitPredictionRound');
  const { data } = await callable({ gameId, round, picks });
  return data;
}

export async function revealGame(gameId: string): Promise<void> {
  const gameRef = doc(db, 'predictionGames', gameId);
  const gameSnap = await getDoc(gameRef);
  if (!gameSnap.exists()) throw new Error('Game not found');

  const gameData = gameSnap.data();
  const answerSnap = await getDoc(answerRef(gameId));
  if (!answerSnap.exists()) throw new Error('Answer song not locked');
  const answerId: string = answerSnap.data().requestId;
  const now = new Date().toISOString();

  // Get all players who made it to round 4 (submitted a final pick)
  const finalistsQuery = query(
    collection(db, 'predictionSelections'),
    where('eventId', '==', gameData.eventId),
    where('gameId', '==', gameId),
    where('round3Eliminated', '==', false)
  );
  const finalistsSnap = await getDocs(finalistsQuery);
  const finalists = finalistsSnap.docs.map(docToSelection);

  // Check round 4 picks against the answer
  const correctPickers = finalists.filter(s => s.round4Picks && s.round4Picks[0] === answerId);

  // Mark each finalist's round4Correct field
  for (const finalist of finalists) {
    const isCorrect = finalist.round4Picks?.[0] === answerId;
    await updateDoc(doc(db, 'predictionSelections', finalist.id), {
      round4Correct: isCorrect,
      updatedAt: now,
    });
  }

  let winnerIds: string[] = [];
  let winnerNicknames: string[] = [];

  if (correctPickers.length === 0) {
    // No one picked correctly
  } else if (gameData.tieBreakMethod === 'shared') {
    winnerIds = correctPickers.map(s => s.attendeeId);
    winnerNicknames = correctPickers.map(s => attendeeDisplayName(s.nickname, s.attendeeId));
  } else if (gameData.tieBreakMethod === 'speed') {
    const sorted = [...correctPickers].sort((a, b) => {
      const aTime = a.finishedAt || '';
      const bTime = b.finishedAt || '';
      return aTime.localeCompare(bTime);
    });
    winnerIds = [sorted[0].attendeeId];
    winnerNicknames = [attendeeDisplayName(sorted[0].nickname, sorted[0].attendeeId)];
  } else {
    const randomIndex = Math.floor(Math.random() * correctPickers.length);
    winnerIds = [correctPickers[randomIndex].attendeeId];
    winnerNicknames = [attendeeDisplayName(correctPickers[randomIndex].nickname, correctPickers[randomIndex].attendeeId)];
  }

  if (winnerIds.length > 0 && gameData.prizeId) {
    await awardPrize(gameData.prizeId, winnerIds[0], winnerNicknames[0]);
  }

  await updateDoc(gameRef, {
    status: 'revealing',
    winnerIds,
    winnerNicknames,
    revealedSongRequestId: answerId,
    revealedAt: now,
    updatedAt: now,
  });
}

export async function completeGame(gameId: string): Promise<void> {
  const gameRef = doc(db, 'predictionGames', gameId);
  const gameSnap = await getDoc(gameRef);
  if (!gameSnap.exists()) throw new Error('Game not found');

  const gameData = gameSnap.data();
  const now = new Date().toISOString();

  await updateDoc(gameRef, {
    status: 'completed',
    updatedAt: now,
  });

  await updateDoc(doc(db, 'events', gameData.eventId), {
    'settings.hideRankingInfo': gameData.previousHideRankingInfo,
    'settings.predictionGameActive': false,
    'settings.predictionGameId': null,
    updatedAt: serverTimestamp(),
  });
}

export async function cancelGame(gameId: string): Promise<void> {
  const gameRef = doc(db, 'predictionGames', gameId);
  const gameSnap = await getDoc(gameRef);
  if (!gameSnap.exists()) throw new Error('Game not found');

  const gameData = gameSnap.data();
  const now = new Date().toISOString();

  await updateDoc(gameRef, {
    status: 'cancelled',
    updatedAt: now,
  });

  await updateDoc(doc(db, 'events', gameData.eventId), {
    'settings.hideRankingInfo': gameData.previousHideRankingInfo,
    'settings.predictionGameActive': false,
    'settings.predictionGameId': null,
    updatedAt: serverTimestamp(),
  });
}

export function subscribeToPredictionGame(
  eventId: string,
  callback: (game: PredictionGame | null) => void
): Unsubscribe {
  const q = query(
    collection(db, 'predictionGames'),
    where('eventId', '==', eventId)
  );
  return onSnapshot(q, (snapshot) => {
    const activeGame = snapshot.docs
      .map(docToGame)
      .find(g => !['completed', 'cancelled'].includes(g.status));
    callback(activeGame || null);
  });
}

export function subscribeToMySelection(
  gameId: string,
  attendeeId: string,
  callback: (selection: PredictionSelection | null) => void
): Unsubscribe {
  const selectionId = `${gameId}_${attendeeId}`;
  return onSnapshot(doc(db, 'predictionSelections', selectionId), (snap) => {
    if (!snap.exists()) {
      callback(null);
      return;
    }
    callback(docToSelection(snap));
  });
}

// DJ only: attendees can't read the answer
export function subscribeToPredictionAnswer(
  gameId: string,
  callback: (answer: PredictionAnswer | null) => void
): Unsubscribe {
  return onSnapshot(answerRef(gameId), (snap) => {
    if (!snap.exists()) {
      callback(null);
      return;
    }
    const data = snap.data();
    callback({ eventId: data.eventId, requestId: data.requestId, lockedAt: data.lockedAt });
  }, (error) => {
    console.error('[prediction] Answer listener failed:', error);
    callback(null);
  });
}

export function subscribeToNowPlaying(
  eventId: string,
  callback: (requestId: string | null) => void
): Unsubscribe {
  const q = query(
    collection(db, 'requests'),
    where('eventId', '==', eventId),
    where('status', '==', 'nowPlaying')
  );
  return onSnapshot(q, (snapshot) => {
    if (snapshot.empty) {
      callback(null);
      return;
    }
    callback(snapshot.docs[0].id);
  });
}

export async function getGameHistory(eventId: string): Promise<PredictionGame[]> {
  const q = query(
    collection(db, 'predictionGames'),
    where('eventId', '==', eventId),
    orderBy('createdAt', 'desc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(docToGame);
}
